import React, { useState } from 'react';
import './AdminSidebar.css';
import IconButton from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import SidebarLogoImage from '../../../assets/Admin/LoginImages/logo-with-name.png';
import { useUserExpended } from '../../../context/UserExpendedContext';
import { usePaymentExpended } from '../../../context/PaymentExpandedContext';
import { useFeeSettingsExpended } from '../../../context/FeeSettingsContext';
import LogoutIcon from '../../../assets/Admin/AdminSidebar/logout_icon.svg';
import DashboardIcon from '../../../assets/Admin/AdminSidebar/dashboard_icon.svg';
import UserManagementIcon from '../../../assets/Admin/AdminSidebar/user.svg';
import PaymentIcon from '../../../assets/Admin/AdminSidebar/payment_icon.svg';
import FeeSettingsIcon from '../../../assets/Admin/AdminSidebar/fee_icon.svg';
import RequestIcon from '../../../assets/Admin/AdminSidebar/request_icon.svg';
import SettingsIcon from '../../../assets/Admin/AdminSidebar/setting.svg';
import RBACIcon from '../../../assets/Admin/AdminSidebar/rbac.svg';
import { Screenmirroring, People, UserOctagon } from 'iconsax-react';

import axios from 'axios';
import Cookies from 'js-cookie';
import { useEffect, useRef } from 'react';
import { axiosInstance } from '../../../utils/tokenUtils';
// import { useAuth } from '../../../context/AuthContext';

const AdminSidebar = () => {
  const [userExpanded, setUserExpanded] = useUserExpended();
  const [paymentExpanded, setPaymentExpanded] = usePaymentExpended();
  const [feeSettingsExpanded, setFeeSettingsExpanded] = useFeeSettingsExpended();
  const [requestExpanded, setRequestExpanded] = useState(false);
  const [organisationExpanded, setOrganisationExpanded] = useState(false);
  const [bannerCoupenExpanded, setBannerCoupenExpanded] = useState(false);
  const location = useLocation();

  const navigate = useNavigate();
  const SERVER_BASE_URL = process.env.REACT_APP_SERVER_API_URL;
  // const [auth] = useAuth();
  const [rbacMe, setRbacMe] = useState(null);
  const fetchedRef = useRef(false);

  // Define route mappings for nested paths
  const routeMappings = {
    // Dashboard routes
    '/admin/dashboard': ['/admin/dashboard'],

    // Request routes
    '/admin/request': ['/admin/request', '/admin/request-details'],

    // // Subject Request routes
    // '/admin/subject-request': [
    //   '/admin/subject-request',
    //   '/admin/subject-request-details',
    // ],

    //organisation routes
    '/admin/organisation': ['/admin/organisation'],
    '/admin/transaction-records': ['/admin/transaction-records'],

    // Teacher routes
    '/admin/users/instructor': ['/admin/users/instructor', '/admin/users/instructor-details'],

    // Learner routes (renamed from student)
    '/admin/users/learner': ['/admin/users/learner', '/admin/users/learner-details'],

    // Payment management routes
    '/admin/payments/transactions': [
      '/admin/payments/transactions',
      '/admin/payments/transactions-details',
    ],
    '/admin/payments/disputes': ['/admin/payments/disputes', '/admin/payments/disputes-details'],
    '/admin/support': ['/admin/support', '/admin/support-detail'],
    '/admin/payments/payout': ['/admin/payments/payout', '/admin/payments/payout-details'],
    '/admin/payments/refund': ['/admin/payments/refund'],
    // Fee settings routes
    '/admin/fee-settings/platform-charges': ['/admin/fee-settings/platform-charges'],
    '/admin/fee-settings/penalties-charges': ['/admin/fee-settings/penalties-charges'],

    // Settings routes
    '/admin/settings': ['/admin/settings'],

    // Banner routes
    '/admin/banner': ['/admin/banner'],

    // Coupen routes
    '/admin/coupons': ['/admin/coupons'],

    // Subject Request routes
    '/admin/subject-request': ['/admin/subject-request', '/admin/subject-request-details'],

    // RBAC routes
    '/admin/rbac': ['/admin/rbac'],
  };

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    let cachedData = null;
    try {
      const cached = sessionStorage.getItem('rbac_admin_me');
      if (cached) {
        cachedData = JSON.parse(cached);
        setRbacMe(cachedData);
      }
    } catch (_) {}

    (async () => {
      try {
        const { data } = await axiosInstance.get('/admin/rbac/admin/me');
        setRbacMe(data);
        try {
          sessionStorage.setItem('rbac_admin_me', JSON.stringify(data));
        } catch (_) {}
      } catch (_) {
        if (!cachedData) setRbacMe({ roles: [], permissions: [] });
      }
    })();
  }, []);

  // Helper function to check if user has a specific permission
  const hasPermission = permission => {
    if (!rbacMe || !Array.isArray(rbacMe.permissions)) return false;
    return rbacMe.permissions.includes(permission);
  };

  const roles = Array.isArray(rbacMe?.roles) ? rbacMe.roles : [];
  const isSuperAdmin = roles.includes('super_admin');
  const isAdmin = roles.includes('admin');
  const isFinanceManager = roles.includes('finance_officer');
  const isComplianceAuditor = roles.includes('compliance_auditor');

  const showDashboard = isSuperAdmin || isAdmin || isFinanceManager;

  const showUserManagement = isSuperAdmin || isAdmin || hasPermission('view_users');

  const showRequest =
    !isComplianceAuditor &&
    (isSuperAdmin ||
      isAdmin ||
      hasPermission('view_subject_requests') ||
      hasPermission('view_tag_requests'));

  const showOrganisation = isSuperAdmin || isAdmin || hasPermission('view_organisations');

  const showPayments =
    isSuperAdmin ||
    isAdmin ||
    hasPermission('view_transactions') ||
    hasPermission('view_disputes') ||
    hasPermission('view_support_tickets');

  const showFeeSettings = isSuperAdmin || isAdmin;

  const showBannerCoupon =
    isSuperAdmin || isAdmin || hasPermission('view_banners') || hasPermission('view_coupons');

  const showSettings = true;

  const showRBAC = isSuperAdmin || hasPermission('view_rbac');

  const getLinkClassName = path => {
    const relatedPaths = routeMappings[path] || [path];

    if (path === '/admin/dashboard') {
      const anySectionExpanded =
        userExpanded ||
        paymentExpanded ||
        feeSettingsExpanded ||
        requestExpanded ||
        organisationExpanded ||
        bannerCoupenExpanded;

      if (anySectionExpanded) {
        return 'single-nav-link';
      }
    }

    if (
      path === '/admin/users/instructor' ||
      path === '/admin/users/learner' ||
      path === '/admin/payments/transactions' ||
      path === '/admin/payments/disputes' ||
      path === '/admin/payments/payout' ||
      path === '/admin/payments/refund' ||
      path === '/admin/support' ||
      path === '/admin/fee-settings/platform-charges' ||
      path === '/admin/fee-settings/penalties-charges' ||
      path === '/admin/subject-request' ||
      path === '/admin/request' ||
      path === '/admin/requested-tag' ||
      path === '/admin/organisation' ||
      path === '/admin/transaction-records' ||
      path === '/admin/coupons' ||
      path === '/admin/banner'
    ) {
      const isActive = relatedPaths.some(
        routePath =>
          location.pathname === routePath || location.pathname.startsWith(routePath + '/')
      );
      return isActive ? 'user-nav-link active' : 'user-new-nav-link';
    }

    // Default handling for other routes
    const isActive = relatedPaths.some(
      routePath => location.pathname === routePath || location.pathname.startsWith(routePath + '/')
    );

    return isActive ? 'single-nav-link active' : 'single-nav-link';
  };

  // Modified toggleUsersExpanded function
  const toggleUsersExpanded = () => {
    setUserExpanded(!userExpanded);
    // Close payment expanded section when users section is opened
    setPaymentExpanded(false);
    setFeeSettingsExpanded(false);
    setRequestExpanded(false);
    setOrganisationExpanded(false);
    setBannerCoupenExpanded(false);
  };

  const togglePaymentExpanded = () => {
    setPaymentExpanded(!paymentExpanded);
    setUserExpanded(false);
    setFeeSettingsExpanded(false);
    setRequestExpanded(false);
    setOrganisationExpanded(false);
    setBannerCoupenExpanded(false);
  };
  const toggleFeeSettingsExpanded = () => {
    setFeeSettingsExpanded(!feeSettingsExpanded);
    // Close user expanded section when fee settings section is opened
    setUserExpanded(false);
    setPaymentExpanded(false);
    // Close request expanded section when fee settings section is opened
    setRequestExpanded(false);
    setOrganisationExpanded(false);
    setBannerCoupenExpanded(false);
  };
  const toggleRequestExpanded = () => {
    setRequestExpanded(!requestExpanded);
    setUserExpanded(false);
    setPaymentExpanded(false);
    setFeeSettingsExpanded(false);
    setOrganisationExpanded(false);
    setBannerCoupenExpanded(false);
  };
  const toggleOrganisationExpanded = () => {
    setOrganisationExpanded(!organisationExpanded);
    setRequestExpanded(false);
    setUserExpanded(false);
    setPaymentExpanded(false);
    setFeeSettingsExpanded(false);
    setBannerCoupenExpanded(false);
  };

  const toggleBannerCoupenExpanded = () => {
    setBannerCoupenExpanded(!bannerCoupenExpanded);
    setUserExpanded(false);
    setPaymentExpanded(false);
    setFeeSettingsExpanded(false);
    setRequestExpanded(false);
    setOrganisationExpanded(false);
  };
  const handleLinkClick = path => {
    if (
      path !== '/admin/users/instructor' &&
      path !== '/admin/users/learner' &&
      path !== '/admin/payments/transactions' &&
      path !== '/admin/payments/disputes' &&
      path !== '/admin/payments/payout' &&
      path !== '/admin/payments/refund' &&
      path !== '/admin/support' &&
      path !== '/admin/fee-settings/platform-charges' &&
      path !== '/admin/fee-settings/penalties-charges' &&
      path !== '/admin/request' &&
      path !== '/admin/subject-request' &&
      path !== '/admin/requested-tag' &&
      path !== '/admin/organisation' &&
      path !== '/admin/transaction-records' &&
      path !== '/admin/rbac'
    ) {
      setUserExpanded(false);
      setPaymentExpanded(false);
      setFeeSettingsExpanded(false);
      setRequestExpanded(false);
      setOrganisationExpanded(false);
    }
  };

  // Check if a path is active
  const isPathActive = path => {
    const relatedPaths = routeMappings[path] || [path];
    return relatedPaths.some(
      routePath => location.pathname === routePath || location.pathname.startsWith(routePath + '/')
    );
  };

  const isUserManagementActive = () => {
    return isPathActive('/admin/users/instructor') || isPathActive('/admin/users/learner');
  };

  const isPaymentActive = () => {
    return (
      isPathActive('/admin/payments/transactions') ||
      isPathActive('/admin/payments/disputes') ||
      isPathActive('/admin/payments/payout') ||
      isPathActive('/admin/payments/refund') ||
      isPathActive('/admin/support')
    );
  };

  const isFeeSettingsActive = () => {
    return (
      isPathActive('/admin/fee-settings/platform-charges') ||
      isPathActive('/admin/fee-settings/penalties-charges')
    );
  };

  const isBannerCoupenActive = () => {
    return isPathActive('/admin/banner') || isPathActive('/admin/coupons');
  };

  // Check if any request path is active
  const isRequestActive = () => {
    return (
      isPathActive('/admin/request') ||
      isPathActive('/admin/subject-request') ||
      isPathActive('/admin/requested-tag')
    );
  };

  const isOrganisationActive = () => {
    return isPathActive('/admin/organisation') || isPathActive('/admin/transaction-records');
  };

  const handleLogout = async () => {
    try {
      const { data } = await axios.post(`${SERVER_BASE_URL}/auth/jwt/logout`);

      if (data) {
        Cookies.remove('access_token');

        Cookies.remove('refresh_token');

        Cookies.remove('user_type');

        Cookies.remove('first_name');

        Cookies.remove('last_name');

        Cookies.remove('email');

        Cookies.remove('profile_image');

        // Clear RBAC cache on logout
        try {
          sessionStorage.removeItem('rbac_admin_me');
        } catch (_) {}

        navigate('/');
      }
    } catch (error) {
      console.log(error);
    }
  };

  const rbacReady = rbacMe !== null;

  const submenuVariants = {
    collapsed: {
      height: 0,
      opacity: 0,
      transition: {
        duration: 0.1,
        ease: 'easeIn',
      },
    },
    expanded: {
      height: 'auto',
      opacity: 1,
      transition: {
        duration: 0.18,
        ease: 'easeOut',
      },
    },
  };

  const submenuItemVariants = {
    collapsed: {
      opacity: 0,
      x: -2,
      transition: {
        duration: 0.1,
        ease: 'easeIn',
      },
    },
    expanded: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.16,
        ease: 'easeOut',
      },
    },
  };

  return (
    <Box className="admin-sidebar-container" data-testid="admin-sidebar">
      <Box className="d-block px-4 pt-3 pb-4 mb-2 ">
        <IconButton
          sx={{
            '&:hover': {
              backgroundColor: 'transparent',
              cursor: 'default',
            },
            padding: 0,
          }}
          data-testid="sidebar-logo-button"
          disableRipple
        >
          <div className="admin-sidebar-logo-container">
            <img
              src={SidebarLogoImage}
              alt="sidebar-logo-image"
              data-testid="sidebar-logo"
              className="h-[18px]"
            />
          </div>
        </IconButton>
      </Box>
      <ul
        className="nav nav-pills nav-flush flex-column mb-auto"
        style={{
          maxHeight: '80vh',
          overflowX: 'hidden',
          overflowY: 'scroll',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: '3px',
        }}
      >
        {rbacReady && (
          <>
            {showDashboard && (
              <li className="single-nav-item">
                <Link
                  to="/admin/dashboard"
                  className={getLinkClassName('/admin/dashboard')}
                  onClick={() => handleLinkClick('/admin/dashboard')}
                >
                  <div className="single-nav-link">
                    <img src={DashboardIcon} alt="dashboard-icon" className="nav-icon" />
                    <span>Dashboard</span>
                  </div>
                </Link>
              </li>
            )}

            {showRequest && (
              <li className="single-nav-item">
                <div
                  className={`users-nav-link ${requestExpanded || isRequestActive() ? 'active' : ''}`}
                  onClick={toggleRequestExpanded}
                >
                  <img src={RequestIcon} alt="request-icon" className="nav-icon" />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div className="user-management-text">Request</div>
                    {requestExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </div>
                </div>
                <AnimatePresence initial={false}>
                  {requestExpanded && (
                    <motion.ul
                      className="nav nav-pills nav-flush flex-column subtab-list"
                      initial="collapsed"
                      animate="expanded"
                      exit="collapsed"
                      variants={submenuVariants}
                      style={{ overflow: 'hidden', willChange: 'height, opacity' }}
                    >
                      <motion.li className="single-nav-item" variants={submenuItemVariants}>
                        <Link
                          to="/admin/request"
                          className={getLinkClassName('/admin/request')}
                          onClick={() => handleLinkClick('/admin/request')}
                        >
                          <div className="single-subtab-nav-link subtab">
                            <span>Instructor Request</span>
                          </div>
                        </Link>
                      </motion.li>
                      <motion.li className="single-nav-item" variants={submenuItemVariants}>
                        <Link
                          to="/admin/subject-request"
                          className={getLinkClassName('/admin/subject-request')}
                        >
                          <div className="single-subtab-nav-link subtab">
                            <span>Subject Request</span>
                          </div>
                        </Link>
                      </motion.li>
                      <motion.li className="single-nav-item-sub" variants={submenuItemVariants}>
                        <Link
                          to="/admin/requested-tag"
                          className={getLinkClassName('/admin/requested-tag')}
                        >
                          <div className="single-subtab-nav-link subtab">
                            <span>Requested Tag</span>
                          </div>
                        </Link>
                      </motion.li>
                    </motion.ul>
                  )}
                </AnimatePresence>
              </li>
            )}

            {showOrganisation && (
              <li className="single-nav-item">
                <div
                  className={`users-nav-link ${organisationExpanded || isOrganisationActive() ? 'active' : ''}`}
                  onClick={toggleOrganisationExpanded}
                >
                  <People
                    size={16}
                    variant="Outline"
                    className="nav-icon"
                    color={organisationExpanded || isOrganisationActive() ? '#fff' : '#949da6'}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div className="user-management-text">Organisation</div>
                    {organisationExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </div>
                </div>
                <AnimatePresence initial={false}>
                  {organisationExpanded && (
                    <motion.ul
                      className="nav nav-pills nav-flush flex-column subtab-list"
                      initial="collapsed"
                      animate="expanded"
                      exit="collapsed"
                      variants={submenuVariants}
                      style={{ overflow: 'hidden', willChange: 'height, opacity' }}
                    >
                      <motion.li className="single-nav-item" variants={submenuItemVariants}>
                        <Link
                          to="/admin/organisation"
                          className={getLinkClassName('/admin/organisation')}
                          onClick={() => handleLinkClick('/admin/organisation')}
                        >
                          <div className="single-subtab-nav-link subtab">
                            <span>Manage & Disburse</span>
                          </div>
                        </Link>
                      </motion.li>
                      <motion.li className="single-nav-item-sub" variants={submenuItemVariants}>
                        <Link
                          to="/admin/transaction-records"
                          className={getLinkClassName('/admin/transaction-records')}
                        >
                          <div className="single-subtab-nav-link subtab">
                            <span>Transaction Records</span>
                          </div>
                        </Link>
                      </motion.li>
                    </motion.ul>
                  )}
                </AnimatePresence>
              </li>
            )}

            {showUserManagement && (
              <li className="single-nav-item">
                <div
                  className={`users-nav-link ${userExpanded || isUserManagementActive() ? 'active' : ''}`}
                  onClick={toggleUsersExpanded}
                >
                  <img src={UserManagementIcon} alt="user-management-icon" className="nav-icon" />
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <div className="user-management-text">User Management</div>
                    {userExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </div>
                </div>
                <AnimatePresence initial={false}>
                  {userExpanded && (
                    <motion.ul
                      className="nav nav-pills nav-flush flex-column subtab-list"
                      initial="collapsed"
                      animate="expanded"
                      exit="collapsed"
                      variants={submenuVariants}
                      style={{ overflow: 'hidden', willChange: 'height, opacity' }}
                    >
                      <motion.li className="single-nav-item" variants={submenuItemVariants}>
                        <Link
                          to="/admin/users/instructor"
                          className={getLinkClassName('/admin/users/instructor')}
                        >
                          <div className="single-subtab-nav-link subtab">
                            <span>Instructor</span>
                          </div>
                        </Link>
                      </motion.li>
                      <motion.li className="single-nav-item-sub " variants={submenuItemVariants}>
                        <Link
                          to="/admin/users/learner"
                          className={getLinkClassName('/admin/users/learner')}
                        >
                          <div className="single-subtab-nav-link subtab">
                            <span>Learner</span>
                          </div>
                        </Link>
                      </motion.li>
                    </motion.ul>
                  )}
                </AnimatePresence>
              </li>
            )}

            {showFeeSettings && (
              <li className="single-nav-item">
                <div
                  className={`users-nav-link ${feeSettingsExpanded || isFeeSettingsActive() ? 'active' : ''}`}
                  onClick={toggleFeeSettingsExpanded}
                >
                  <img src={FeeSettingsIcon} alt="fee-settings-icon" className="nav-icon" />
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <div className="user-management-text">Fee Settings</div>
                    {feeSettingsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </div>
                </div>
                <AnimatePresence initial={false}>
                  {feeSettingsExpanded && (
                    <motion.ul
                      className="nav nav-pills nav-flush flex-column subtab-list"
                      initial="collapsed"
                      animate="expanded"
                      exit="collapsed"
                      variants={submenuVariants}
                      style={{ overflow: 'hidden', willChange: 'height, opacity' }}
                    >
                      <motion.li className="single-nav-item" variants={submenuItemVariants}>
                        <Link
                          to="/admin/fee-settings/platform-charges"
                          className={getLinkClassName('/admin/fee-settings/platform-charges')}
                        >
                          <div className="single-subtab-nav-link subtab">
                            <span>Platform Charges</span>
                          </div>
                        </Link>
                      </motion.li>
                      <motion.li className="single-nav-item-sub " variants={submenuItemVariants}>
                        <Link
                          to="/admin/fee-settings/penalties-charges"
                          className={getLinkClassName('/admin/fee-settings/penalties-charges')}
                        >
                          <div className="single-subtab-nav-link subtab">
                            <span>Penalties Charges</span>
                          </div>
                        </Link>
                      </motion.li>
                    </motion.ul>
                  )}
                </AnimatePresence>
              </li>
            )}

            {showPayments && (
              <li className="single-nav-item">
                <div
                  className={`users-nav-link ${paymentExpanded || isPaymentActive() ? 'active' : ''}`}
                  onClick={togglePaymentExpanded}
                >
                  <img src={PaymentIcon} alt="payment-icon" className="nav-icon" />
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <div className="user-management-text">Payment</div>
                    {paymentExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </div>
                </div>
                <AnimatePresence initial={false}>
                  {paymentExpanded && (
                    <motion.ul
                      className="nav nav-pills nav-flush flex-column subtab-list"
                      initial="collapsed"
                      animate="expanded"
                      exit="collapsed"
                      variants={submenuVariants}
                      style={{ overflow: 'hidden', willChange: 'height, opacity' }}
                    >
                      <motion.li className="single-nav-item" variants={submenuItemVariants}>
                        <Link
                          to="/admin/payments/transactions"
                          className={getLinkClassName('/admin/payments/transactions')}
                        >
                          <div className="single-subtab-nav-link subtab">
                            <span>PayIn</span>
                          </div>
                        </Link>
                      </motion.li>
                      <motion.li className="single-nav-item" variants={submenuItemVariants}>
                        <Link
                          to="/admin/payments/payout"
                          className={getLinkClassName('/admin/payments/payout')}
                        >
                          <div className="single-subtab-nav-link subtab">
                            <span>PayOut</span>
                          </div>
                        </Link>
                      </motion.li>
                      <motion.li className="single-nav-item" variants={submenuItemVariants}>
                        <Link
                          to="/admin/payments/disputes"
                          className={getLinkClassName('/admin/payments/disputes')}
                        >
                          <div className="single-subtab-nav-link subtab">
                            <span>Disputes</span>
                          </div>
                        </Link>
                      </motion.li>
                      <motion.li className="single-nav-item-sub" variants={submenuItemVariants}>
                        <Link
                          to="/admin/payments/refund"
                          className={getLinkClassName('/admin/payments/refund')}
                        >
                          <div className="single-subtab-nav-link subtab">
                            <span>Refund</span>
                          </div>
                        </Link>
                      </motion.li>
                    </motion.ul>
                  )}
                </AnimatePresence>
              </li>
            )}

            {showBannerCoupon && (
              <li className="single-nav-item">
                <div
                  className={`users-nav-link ${bannerCoupenExpanded || isBannerCoupenActive() ? 'active' : ''}`}
                  onClick={toggleBannerCoupenExpanded}
                >
                  <Screenmirroring
                    size={16}
                    variant="Outline"
                    className="nav-icon"
                    color={bannerCoupenExpanded || isBannerCoupenActive() ? '#fff' : '#949da6'}
                  />
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <div className="user-management-text">Banner & Coupon</div>
                    {bannerCoupenExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </div>
                </div>
                <AnimatePresence initial={false}>
                  {bannerCoupenExpanded && (
                    <motion.ul
                      className="nav nav-pills nav-flush flex-column subtab-list"
                      initial="collapsed"
                      animate="expanded"
                      exit="collapsed"
                      variants={submenuVariants}
                      style={{ overflow: 'hidden', willChange: 'height, opacity' }}
                    >
                      <motion.li className="single-nav-item" variants={submenuItemVariants}>
                        <Link to="/admin/banner" className={getLinkClassName('/admin/banner')}>
                          <div className="single-subtab-nav-link subtab">
                            <span>Banner</span>
                          </div>
                        </Link>
                      </motion.li>
                      <motion.li className="single-nav-item-sub " variants={submenuItemVariants}>
                        <Link to="/admin/coupons" className={getLinkClassName('/admin/coupons')}>
                          <div className="single-subtab-nav-link subtab">
                            <span>Coupon</span>
                          </div>
                        </Link>
                      </motion.li>
                    </motion.ul>
                  )}
                </AnimatePresence>
              </li>
            )}

            {showSettings && (
              <li className="single-nav-item">
                <Link
                  to="/admin/settings"
                  className={getLinkClassName('/admin/settings')}
                  onClick={() => handleLinkClick('/admin/settings')}
                >
                  <div className="single-nav-link">
                    <img src={SettingsIcon} alt="settings-icon" className="nav-icon" />
                    <span>Settings</span>
                  </div>
                </Link>
              </li>
            )}
            {showRBAC && (
              <li className="single-nav-item">
                <Link
                  to="/admin/rbac"
                  className={getLinkClassName('/admin/rbac')}
                  onClick={() => handleLinkClick('/admin/rbac')}
                >
                  <div className="single-nav-link">
                    <UserOctagon
                      size={16}
                      variant="Outline"
                      className="nav-icon"
                      color={
                        location.pathname === '/admin/rbac' ||
                        location.pathname.startsWith('/admin/rbac/')
                          ? '#fff'
                          : '#949da6'
                      }
                    />
                    <span>RBAC</span>
                  </div>
                </Link>
              </li>
            )}
          </>
        )}
      </ul>

      <div className="sidebar-logout-container">
        <div className="logout-button" onClick={handleLogout}>
          <img src={LogoutIcon} alt="logout-icon" className="nav-icon" />
          <span>Logout</span>
        </div>
      </div>
    </Box>
  );
};

export default AdminSidebar;
